import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Card,
  CardBody,
  useToast,
  Textarea,
  Checkbox,
  CheckboxGroup,
  Stack,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  Collapse,
  IconButton,
  Image,
  Tag,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import logoIcon from '../assets/logo/aplica-ia-icone.svg';
import { useLocation } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import { FiSend, FiTrash2, FiSearch, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { chatApi, collectionsApi } from '../services/api';
import ChatMessage from '../components/ChatMessage';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  sources?: Array<{
    filename: string;
    collection: string;
    relevance_score: number;
    chunk_id: number;
    text_preview: string;
    source_file?: string;
    document_type?: string;
    file_size?: string;
    last_modified?: string;
    content_type?: string;
    processing_mode?: string;
    has_ai_analysis?: boolean;
    ai_tokens_used?: number;
    processing_date?: string;
    file_hash?: string;
    is_recent?: boolean;
    reliability_score?: number;
  }>;
  model_used?: string;
  tokens_used?: number;
  performance?: {
    total_duration_seconds?: number;
    api_duration_seconds?: number;
    tokens_per_second?: number;
  };
  error_info?: {
    error_type?: string;
    error_message?: string;
    suggestions?: string[];
    timestamp?: string;
  };
  status?: 'sending' | 'processing' | 'completed' | 'error';
}

const Chat = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [selectedCollections, setSelectedCollections] = useState<string[]>([]);
  const [conversationId] = useState(() => Math.random().toString(36).substring(7));
  const [searchTerm, setSearchTerm] = useState('');
  const [isCollectionsPanelOpen, setIsCollectionsPanelOpen] = useState(true);

  // Configuracoes fixas (ocultas do usuario)
  const selectedModel = 'google/gemini-2.5-flash-lite';
  const maxTokens = 4000;
  const temperature = 0.1;
  const contextLimit = 10;

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const toast = useToast();

  // Base + perguntas sugeridas vindas do Home (atalho da base de exemplo).
  const location = useLocation();
  const navState = (location.state || {}) as { preselect?: string; suggestions?: string[] };
  const [didPreselect, setDidPreselect] = useState(false);
  const suggestions = (navState.suggestions && navState.suggestions.length)
    ? navState.suggestions
    : ['Resuma os principais pontos', 'Quais são os riscos mencionados?', 'O que diz sobre prazos e valores?'];

  // Fetch collections
  const { data: collections = [] } = useQuery(
    'collections',
    collectionsApi.list,
    { refetchInterval: 30000 }
  );

  // Quando a lista carrega, seleciona automaticamente a base indicada pelo Home.
  useEffect(() => {
    if (didPreselect || !navState.preselect) return;
    if (collections.some((c) => c.name === navState.preselect)) {
      setSelectedCollections([navState.preselect]);
      setIsCollectionsPanelOpen(false);
      setDidPreselect(true);
    }
  }, [collections, navState.preselect, didPreselect]);

  // Filtrar collections baseado na busca
  const filteredCollections = collections.filter((collection) =>
    collection.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handler para selecao de collections com auto-close
  const handleCollectionsChange = (values: string[]) => {
    setSelectedCollections(values as string[]);
    // Auto-fechar o painel se tiver pelo menos uma collection selecionada
    if (values.length > 0) {
      setTimeout(() => setIsCollectionsPanelOpen(false), 300);
    }
  };

  // Chat mutation
  const chatMutation = useMutation(
    (request: any) => {
      return chatApi.chatWithConversation(conversationId, request);
    },
    {
      onMutate: () => {
        setMessages(prev =>
          prev.map((msg, index) =>
            index === prev.length - 1 && msg.role === 'user'
              ? { ...msg, status: 'processing' as const }
              : msg
          )
        );
      },
      onSuccess: (data) => {
        const assistantMessage: Message = {
          role: 'assistant',
          content: data.response,
          sources: data.sources,
          model_used: data.model_used,
          tokens_used: data.tokens_used,
          performance: data.performance,
          status: 'completed',
        };

        setMessages(prev => {
          const newMessages = [...prev];
          if (newMessages[newMessages.length - 1]?.status === 'processing') {
            newMessages[newMessages.length - 1] = { ...newMessages[newMessages.length - 1], status: 'completed' };
          }
          return [...newMessages, assistantMessage];
        });
      },
      onError: (error: any) => {
        const errorData = error.response?.data;

        const errorMessage: Message = {
          role: 'assistant',
          content: 'Desculpe, ocorreu um erro ao processar sua mensagem.',
          error_info: {
            error_type: errorData?.error_type || 'unknown_error',
            error_message: errorData?.error_message || errorData?.detail || 'Erro desconhecido',
            suggestions: errorData?.suggestions || [
              'Tente novamente em alguns segundos',
              'Verifique sua conexao com a internet'
            ],
            timestamp: new Date().toISOString()
          },
          status: 'error'
        };

        setMessages(prev => {
          const newMessages = [...prev];
          if (newMessages[newMessages.length - 1]?.status === 'processing') {
            newMessages[newMessages.length - 1] = { ...newMessages[newMessages.length - 1], status: 'completed' };
          }
          return [...newMessages, errorMessage];
        });

        toast({
          title: 'Erro no chat',
          description: errorData?.error_message || errorData?.detail || 'Erro desconhecido',
          status: 'error',
          duration: 5000,
        });
      },
    }
  );

  // Aceita texto direto (perguntas sugeridas) ou usa o campo digitado.
  const handleSendMessage = (overrideText?: string) => {
    const text = (overrideText ?? currentMessage).trim();
    if (!text) {
      toast({
        title: 'Mensagem vazia',
        description: 'Digite uma mensagem para enviar',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    if (selectedCollections.length === 0) {
      toast({
        title: 'Nenhuma base selecionada',
        description: 'Selecione pelo menos uma base de conhecimento para buscar',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    const userMessage: Message = {
      role: 'user',
      content: text,
      status: 'sending'
    };
    setMessages(prev => [...prev, userMessage]);

    chatMutation.mutate({
      message: text,
      collection_names: selectedCollections,
      model: selectedModel,
      max_tokens: maxTokens,
      temperature: temperature,
      context_limit: contextLimit,
    });

    setCurrentMessage('');
  };

  const handleClearConversation = () => {
    setMessages([]);
    chatApi.clearConversation(conversationId).catch(console.error);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Auto scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return (
    <Box>
      <VStack spacing={6} align="stretch" h="calc(100vh - 120px)">
        {/* Header */}
        <HStack justify="space-between" align="center">
          <Box>
            <Heading size="lg">Chat com Documentos</Heading>
            <Text color="gray.600">
              Faca perguntas sobre seus documentos usando IA
            </Text>
          </Box>
          <Button
            leftIcon={<FiTrash2 />}
            colorScheme="red"
            variant="outline"
            size="sm"
            onClick={handleClearConversation}
            isDisabled={messages.length === 0}
          >
            Limpar
          </Button>
        </HStack>

        {/* Collections Selection - Colapsavel com Busca */}
        <Card>
          <CardBody p={3}>
            {/* Header clicavel para expandir/colapsar */}
            <HStack
              justify="space-between"
              cursor="pointer"
              onClick={() => setIsCollectionsPanelOpen(!isCollectionsPanelOpen)}
              _hover={{ bg: 'gray.50' }}
              p={2}
              borderRadius="md"
              transition="all 0.2s"
            >
              <HStack spacing={3}>
                <Text fontWeight="medium">
                  {isCollectionsPanelOpen ? 'Selecione o conhecimento' : 'Conhecimento'}
                </Text>
                {selectedCollections.length > 0 && (
                  <Badge colorScheme="green" fontSize="sm">
                    {selectedCollections.length} selecionada{selectedCollections.length > 1 ? 's' : ''}
                  </Badge>
                )}
              </HStack>
              <IconButton
                aria-label="Alternar bases"
                icon={isCollectionsPanelOpen ? <FiChevronUp /> : <FiChevronDown />}
                size="sm"
                variant="ghost"
              />
            </HStack>

            {/* Conteudo expansivel */}
            <Collapse in={isCollectionsPanelOpen} animateOpacity>
              <VStack spacing={3} mt={3} align="stretch">
                {/* Campo de busca */}
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <FiSearch color="gray" />
                  </InputLeftElement>
                  <Input
                    placeholder="Buscar collections..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="sm"
                  />
                </InputGroup>

                {/* Lista de collections */}
                <CheckboxGroup
                  value={selectedCollections}
                  onChange={handleCollectionsChange}
                >
                  <Box
                    maxHeight="200px"
                    overflowY="auto"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    p={3}
                  >
                    <Stack spacing={2}>
                      {filteredCollections.length === 0 ? (
                        <Text color="gray.500" fontSize="sm">
                          {searchTerm ? 'Nenhuma base encontrada' : 'Nenhuma base disponível'}
                        </Text>
                      ) : (
                        filteredCollections.map((collection) => (
                          <Checkbox key={collection.name} value={collection.name}>
                            <HStack spacing={2}>
                              <Text fontSize="sm">{collection.name}</Text>
                              <Badge size="sm" colorScheme="blue">
                                {collection.vectors_count} vetores
                              </Badge>
                            </HStack>
                          </Checkbox>
                        ))
                      )}
                    </Stack>
                  </Box>
                </CheckboxGroup>

                {/* Mensagem de ajuda */}
                <Text fontSize="xs" color="gray.600">
                  Dica: selecione as bases e o painel fecha automaticamente
                </Text>
              </VStack>
            </Collapse>
          </CardBody>
        </Card>

        {/* Messages Area */}
        <Box flex={1} overflowY="auto" bg="gray.50" borderRadius="lg" p={4}>
          {messages.length === 0 ? (
            <Box textAlign="center" py={12}>
              <VStack spacing={5}>
                <Image src={logoIcon} alt="Aplica IA" boxSize="56px" borderRadius="xl" opacity={0.95} />
                <Box>
                  <Heading size="md" color="gray.700">Converse com o seu conhecimento</Heading>
                  <Text fontSize="sm" color="gray.500" mt={1}>
                    Selecione uma base e faça uma pergunta. Cada resposta cita a fonte.
                  </Text>
                </Box>
                <Wrap justify="center" spacing={2} maxW="lg">
                  {suggestions.map((s) => (
                    <WrapItem key={s}>
                      <Tag
                        size="md"
                        variant="subtle"
                        colorScheme="brand"
                        cursor="pointer"
                        _hover={{ bg: 'brand.100' }}
                        onClick={() => handleSendMessage(s)}
                      >
                        {s}
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
              </VStack>
            </Box>
          ) : (
            <VStack spacing={6} align="stretch">
              {messages.map((message, index) => (
                <ChatMessage
                  key={index}
                  message={message}
                  isLatest={index === messages.length - 1}
                  onRegenerateResponse={() => {
                    if (index === messages.length - 1 && message.role === 'assistant') {
                      const lastUserMessage = messages[index - 1];
                      if (lastUserMessage && lastUserMessage.role === 'user') {
                        setMessages(prev => prev.slice(0, -1));
                        chatMutation.mutate({
                          message: lastUserMessage.content,
                          collection_names: selectedCollections,
                          model: selectedModel,
                          max_tokens: maxTokens,
                          temperature: temperature,
                          context_limit: contextLimit,
                        });
                      }
                    }
                  }}
                  onMessageReaction={(reaction) => {
                    toast({
                      title: reaction === 'like' ? 'Obrigado pelo feedback!' : 'Feedback recebido',
                      description: reaction === 'like' ? 'Sua avaliacao nos ajuda a melhorar' : 'Vamos trabalhar para melhorar',
                      status: reaction === 'like' ? 'success' : 'info',
                      duration: 2000
                    });
                  }}
                />
              ))}
              <div ref={messagesEndRef} />
            </VStack>
          )}
        </Box>

        {/* Input Area */}
        <HStack spacing={4}>
          <Textarea
            placeholder="Digite sua pergunta sobre os documentos..."
            value={currentMessage}
            onChange={(e) => setCurrentMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            resize="none"
            minH="60px"
            maxH="120px"
          />
          <Button
            colorScheme="brand"
            leftIcon={<FiSend />}
            onClick={() => handleSendMessage()}
            isLoading={chatMutation.isLoading}
            isDisabled={!currentMessage.trim() || selectedCollections.length === 0}
            minW="100px"
            h="60px"
          >
            Enviar
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
};

export default Chat;
